import { memo, useMemo, type ReactNode, type FunctionComponent } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
// Skillmore UI Components
import { fontWeightMedium } from '@empathco/ui-components/src/styles/themeOptions';
import { mapChunks, type Values } from '@empathco/ui-components/src/helpers/intl';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import InlineAlert from '@empathco/ui-components/src/elements/InlineAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import CardDeck from '@empathco/ui-components/src/elements/CardDeck';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import ReloadingOverlay from '@empathco/ui-components/src/elements/ReloadingOverlay';
// local imports
import { AdminJob, SortDirection } from '../graphql/types';
import { JobLookupItem } from '../graphql/customTypes';
import { Job } from '../models/job';
import { JobSortExt } from '../constants/jobSort';
import useNonReducedUI from '../constants/managementLevel';
import useCustomerSettings from '../config/customer';
import { hasFilters, JobsIndexFiltersComponent } from './JobsIndexFilters';
import { hasPagination, PaginationControlsComponent } from './PaginationControls';
import ComputeUpdatesOverlay from '../v3/ComputeUpdatesOverlay';
import RoleCard from '../v3/RoleCard';
import JobsTable from '../v3/JobsTable';
import JobMatch from '../widgets/JobMatch';
// SCSS imports
import { topPadding, bottomPadding, dirtyBox } from '@empathco/ui-components/src/styles/modules/ItemsGrid.module.scss';

const titleValues: Values = {
  br: <br/>,
  em: (chunks?: ReactNode): ReactNode => (
    <Box component="span" display="block" fontWeight={fontWeightMedium} color="info.main">
      {mapChunks(chunks)}
    </Box>
  ),
  par: (chunks?: ReactNode): ReactNode => (
    <Box component="span" display="block">
      {mapChunks(chunks)}
    </Box>
  )
};

type RolesGridProps = {
  readonly roles?: (Job | JobLookupItem | AdminJob)[] | null;
  readonly currentRole?: Job | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly dirty?: boolean | null;
  readonly title?: string | null;
  readonly filters?: ReactNode | null;
  readonly pagination?: ReactNode | null;
  readonly dark?: boolean | null;
  readonly table?: boolean | null;
  readonly sortBy?: JobSortExt | null;
  readonly direction?: boolean | null;
  readonly changeSort?: ((sort: JobSortExt, direction: boolean) => void) | null;
  readonly sortDisabled?: boolean | null;
  // in Supervisor mode `onClick` is fired only for jobs with management_level >= 1
  readonly onClick?: ((code: string, job?: Job | JobLookupItem | AdminJob) => void) | null;
  readonly withOpenReqsPopup?: boolean;
  readonly withReloading?: boolean;
  readonly admin?: boolean;
  readonly hrbp?: boolean;
  readonly supervisor?: boolean;
  readonly matchRate?: number | null;
  readonly matchRatePending?: boolean | null;
}

const RolesGrid: FunctionComponent<RolesGridProps> = ({
  roles,
  currentRole,
  pending = false,
  failed = false,
  dirty = false,
  title,
  filters,
  pagination,
  dark = false,
  table = false,
  sortBy,
  direction,
  changeSort,
  sortDisabled = false,
  onClick,
  withOpenReqsPopup = false,
  withReloading = false,
  admin = false,
  hrbp = false,
  supervisor = false,
  matchRate,
  matchRatePending
  // eslint-disable-next-line complexity
}) => {
  const { HAS_JOBS_CHART } = useCustomerSettings();
  const { showNonReducedUI } = useNonReducedUI();

  const withFilters = hasFilters(filters as JobsIndexFiltersComponent);
  const withPagination = hasPagination(pagination as PaginationControlsComponent);
  const loading = withReloading ? pending && !roles : pending || !roles;
  const reloading = Boolean(withReloading && pending && roles);

  const handleSort = useMemo(() => changeSort
    ? (sort: JobSortExt, dir: SortDirection) => changeSort(sort, dir === SortDirection.ascending)
    : undefined, [changeSort]);

  const filtersContent = withFilters ? (
    <CardSection
        flex
        narrow
        top={(!dark && (
          // not JobMatch component is displayed:
          failed || loading || !roles || size(roles) < 1 || !HAS_JOBS_CHART || !currentRole
        )) || (
          handleSort && table && !title
        )}
    >
      {filters}
    </CardSection>
  ) : filters;

  return (
    <Box flexGrow={1} display="flex" flexDirection="column" position="relative">
      {filtersContent}
      <ReloadingOverlay reloading={reloading}>
        {(handleSort && table && (
          <JobsTable
              admin={admin}
              hrbp={hrbp}
              supervisor={supervisor}
              onClick={onClick}
              withOpenReqsPopup={withOpenReqsPopup}
              title={title}
              values={titleValues}
              data={roles}
              pending={loading}
              failed={failed}
              sortBy={sortBy}
              direction={direction}
              changeSort={handleSort}
              disabled={sortDisabled}
          />
        )) || (failed || loading || !roles || size(roles) < 1
          ? (
            <Box
                className={clsx({
                  [topPadding]: withFilters && (!dark || (handleSort && table)),
                  [bottomPadding]: withPagination && (!dark || (handleSort && table)),
                  [dirtyBox]: dirty
                })}
            >
              {(failed && <FetchFailedAlert flat/>) ||
              ((loading || !roles) && <LoadingPlaceholder flat/>) || (
                <>
                  {withFilters ? <Divider/> : undefined}
                  <InlineAlert severity="info" message="jobs_index.empty" flat/>
                </>
              )}
            </Box>
          ) : (HAS_JOBS_CHART && currentRole && (
            <>
              {withFilters ? <Divider/> : undefined}
              <JobMatch
                  title={title}
                  values={titleValues}
                  data={roles as Job[]}
                  currentRole={currentRole}
              />
            </>
          )) || (
            <CardDeck dark={dark}>
              {title ? (
                <BoxTypography
                    width="100%"
                    flexGrow={1}
                    px={2}
                    pb={2}
                    variant="body1"
                    fontStyle="italic"
                    color="text.label"
                >
                  <FormattedMessage id={title} defaultMessage={title} values={titleValues}/>
                </BoxTypography>
              ) : undefined}
              {map(roles, (role) => {
                const forceOpenReqsPopup = !admin && (supervisor || hrbp) && !showNonReducedUI(role);
                return (
                  <RoleCard
                      key={role.id}
                      onClick={forceOpenReqsPopup ? undefined : onClick}
                      withOpenReqsPopup={withOpenReqsPopup || forceOpenReqsPopup}
                      role={role}
                      isEmployee={!supervisor && !hrbp && !admin}
                      depersonalized={hrbp || admin}
                      matchRate={matchRate}
                      matchRatePending={matchRatePending}
                  />
                );
              })}
            </CardDeck>
          )
        )}
        {withPagination ? (
          <CardSection narrow bottom={!dark || (handleSort && table)} flex>
            {pagination}
          </CardSection>
        ) : pagination}
      </ReloadingOverlay>
      {dirty ? <ComputeUpdatesOverlay/> : undefined}
    </Box>
  );
};

export default memo(RolesGrid);
